// src/pages/ManageEventsPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { collection, query, orderBy, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../firebase'; // Adjust path
import { useAuth } from '../context/AuthContext'; // Adjust path
import styles from '../styles/ListPage.module.css'; // Use a generic list page style or create a new one

// Helper to format date string (YYYY-MM-DD) for display
const formatDisplayDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
        const date = new Date(dateString + 'T00:00:00'); // Mitigate timezone issues for display
        if (isNaN(date.getTime())) return 'Invalid Date';
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch (e) {
        return 'Invalid Date';
    }
};

// Helper to get display string for event type
const getDisplayEventType = (eventType) => {
    if (eventType === 'popup') return 'Pop-up';
    // Default to 'Normal' if eventType is not 'popup' or is undefined
    return 'Normal';
};

const ManageEventsPage = () => {
    const { currentUser, userRole } = useAuth();
    const navigate = useNavigate();
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [deletingId, setDeletingId] = useState(null);

    useEffect(() => {
        if (userRole && userRole !== 'Manager' && userRole !== 'Accounts Manager') {
            console.log("Manage Events: Access denied. Redirecting...");
            navigate('/dashboard');
        }
    }, [userRole, navigate]);

    useEffect(() => {
        if (!userRole || userRole === 'Manager' || userRole === 'Accounts Manager') {
            const fetchEvents = async () => {
                setLoading(true);
                setError('');
                try {
                    const eventsCol = collection(db, 'events');
                    const q = query(eventsCol, orderBy('date', 'desc'), orderBy('createdAt', 'desc'));
                    const querySnapshot = await getDocs(q);
                    const fetchedEvents = querySnapshot.docs.map(doc => ({
                        id: doc.id,
                        ...doc.data()
                        // eventType field will be part of doc.data() if it exists
                    }));
                    setEvents(fetchedEvents);
                } catch (err) {
                    console.error("Error fetching events:", err);
                    setError("Failed to load events. Please try again.");
                    setEvents([]);
                } finally {
                    setLoading(false);
                }
            };
            fetchEvents();
        } else {
            setLoading(false);
        }
    }, [userRole]);

    const handleDelete = async (eventId, eventTitle) => {
        if (window.confirm(`Are you sure you want to delete the event "${eventTitle || 'this event'}"?`)) {
            setDeletingId(eventId);
            setError('');
            try {
                const eventDocRef = doc(db, 'events', eventId);
                await deleteDoc(eventDocRef);
                setEvents(prevEvents => prevEvents.filter(event => event.id !== eventId));
                console.log(`Event ${eventId} deleted successfully.`);
            } catch (err) {
                console.error("Error deleting event:", err);
                setError(`Failed to delete event "${eventTitle || ''}". Please try again.`);
            } finally {
                setDeletingId(null);
            }
        }
    };

    if (!userRole && !currentUser) {
        return <div className={styles.loadingIndicator}>Loading user data...</div>;
    }
    if (userRole && userRole !== 'Manager' && userRole !== 'Accounts Manager') {
        return <div className={styles.loadingIndicator}>Access Denied. Redirecting...</div>;
    }
    if (loading) {
        return <div className={styles.loadingIndicator}>Loading events...</div>;
    }

    return (
        <div className={styles.listPageContainer}>
            <div className={styles.header}>
                <h2 className={styles.title}>Manage Events</h2>
                <Link to="/events/new" className={styles.addButton}>
                    Create New Event
                </Link>
            </div>

            {error && <p className={styles.errorMessage}>{error}</p>}

            <div className={styles.tableContainer}>
                <table className={styles.dataTable}>
                    <thead>
                        <tr>
                            <th>Date</th>
                            <th>Title</th>
                            <th>Description</th>
                            <th>Type</th> {/* New Column for Event Type */}
                            <th className={styles.actionsHeader}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {events.length === 0 && !loading && (
                            <tr>
                                <td colSpan="5" className={styles.noDataCell}>No events found.</td> {/* Adjusted colSpan */}
                            </tr>
                        )}
                        {events.map((event) => (
                            <tr key={event.id} className={deletingId === event.id ? styles.deletingRow : ''}>
                                <td>{formatDisplayDate(event.date)}</td>
                                <td>{event.title}</td>
                                <td className={styles.descriptionCell}>{event.description || '-'}</td>
                                <td>{getDisplayEventType(event.eventType)}</td> {/* Display Event Type */}
                                <td className={styles.actionsCell}>
                                    <Link
                                        to={`/events/${event.id}/edit`}
                                        className={`${styles.actionButton} ${styles.editButton}`}
                                        title={`Edit "${event.title}"`}
                                    >
                                        Edit
                                    </Link>
                                    <button
                                        onClick={() => handleDelete(event.id, event.title)}
                                        className={`${styles.actionButton} ${styles.deleteButton}`}
                                        disabled={deletingId === event.id}
                                        title={`Delete "${event.title}"`}
                                    >
                                        {deletingId === event.id ? 'Deleting...' : 'Delete'}
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default ManageEventsPage;